import React, { useCallback } from "react";
import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, Close } from "mosaic-react-icons";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { AI_CONFIDENCE_GRADIENT, KPI_BORDER_COLORS } from "../constants";
import { resolveAiTargetToGridFieldId, stripCdmPrefix } from "../utils";
import MappingDropdown from "../components/MappingDropdown";
import MappingGridToolbar from "../components/MappingGridToolbar";
import StatTile from "../components/StatTile";

const StepMapColumns = ({
    needsMappingCount,
    readyCount,
    skippedCount,
    totalColumns,
    mappings,
    mschFields,
    usedFields,
    aiSuggestedColumns,
    aiAppliedSkippedCsv,
    aiMappingResult,
    showAiMappingDialog,
    originalMappingsRef,
    hideDoNotImport,
    showDoNotImportToggle,
    dgItems,
    dgHeader,
    dgAvailableFilters,
    i18n,
    loading,
    onMappingChange,
    onRevert,
    onDismissAiBanner,
    onApplyAiMappings,
    onCloseAiDialog,
    onToggleHideDoNotImport,
}) => {
    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const value = mappings[csvColumn] ?? null;
            const isAiSuggested = aiSuggestedColumns.has(csvColumn);

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <SovosTypography variant="caption" fontWeight={600} color="text.primary" noWrap>
                            {displayName}
                        </SovosTypography>
                        {isAiSuggested && (
                            <AutoAwesome
                                sx={{ fontSize: 11, color: "primary.main" }}
                                titleAccess={i18n`Applied by AI`}
                            />
                        )}
                    </SovosBox>
                    <MappingDropdown
                        csvColumn={csvColumn}
                        displayName={displayName}
                        value={value}
                        originalValue={originalMappingsRef.current[csvColumn] ?? null}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        aiScore={isAiSuggested ? aiSuggestedColumns.get(csvColumn) : null}
                        onChange={onMappingChange}
                        onRevert={onRevert}
                        i18n={i18n}
                    />
                </SovosBox>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mappings, usedFields, mschFields, aiSuggestedColumns, onMappingChange, onRevert]
    );

    if (loading) {
        return (
            <SovosBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    minHeight: "calc(100vh - 280px)",
                }}
            >
                <CircularProgress size={28} />
                <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                    {i18n`Loading mapping data…`}
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", gap: 1.5, mb: aiSuggestedColumns.size > 0 ? 1.5 : 3 }}>
                <StatTile label={i18n`Columns need to be mapped`} value={needsMappingCount} borderColor={KPI_BORDER_COLORS[0]} />
                <StatTile label={i18n`Ready for next step`} value={readyCount} borderColor={KPI_BORDER_COLORS[1]} />
                <StatTile label={i18n`Columns skipped mapping`} value={skippedCount} borderColor={KPI_BORDER_COLORS[2]} />
                <StatTile label={i18n`All columns`} value={totalColumns} borderColor={KPI_BORDER_COLORS[3]} />
            </SovosBox>

            {aiSuggestedColumns.size > 0 && (
                <SovosBox sx={{ mb: 3, border: "1px solid", borderColor: "primary.light", borderRadius: 1, overflow: "hidden" }}>
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            bgcolor: "primary.50",
                            borderBottom: "1px solid",
                            borderColor: "primary.light",
                        }}
                    >
                        <AutoAwesome sx={{ fontSize: 14, color: "primary.main" }} />
                        <SovosTypography variant="caption" fontWeight={600} color="primary.main" sx={{ flex: 1 }}>
                            {i18n`${aiSuggestedColumns.size} high-confidence field mappings were suggested by Sovi AI.`}{" "}
                            {i18n`Review and adjust the suggested mappings as needed.`}
                        </SovosTypography>
                        <SovosIconButton size="small" tooltipText={i18n`Dismiss`} onClick={onDismissAiBanner}>
                            <Close sx={{ fontSize: 14 }} />
                        </SovosIconButton>
                    </SovosBox>
                    {aiAppliedSkippedCsv > 0 && (
                        <SovosBox sx={{ px: 1.5, py: 0.75, bgcolor: "warning.light", borderBottom: "1px solid", borderColor: "warning.light" }}>
                            <SovosTypography variant="caption" color="warning.dark">
                                {i18n`${aiAppliedSkippedCsv} proposed rows were not applied because their CSV column names did not match this file.`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 0.5,
                            bgcolor: "action.selected",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>{i18n`CSV column`}</SovosTypography>
                        <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1 }}>{i18n`Map to`}</SovosTypography>
                        <SovosTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ width: 100, flexShrink: 0, textAlign: "right" }}
                            title={i18n`How strong this proposed link is (0–100%)`}
                        >
                            {i18n`Confidence`}
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {[...aiSuggestedColumns.entries()].map(([col, score], idx) => {
                            const targetField = mappings[col];
                            const field = mschFields.find((f) => f.id === targetField);
                            return (
                                <SovosBox
                                    key={col}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 0.75,
                                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <SovosTypography variant="caption" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>
                                        {stripCdmPrefix(col)}
                                    </SovosTypography>
                                    <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                                    <SovosTypography variant="caption" color="primary.main" sx={{ flex: 1 }}>
                                        {field?.name || stripCdmPrefix(targetField) || "—"}
                                    </SovosTypography>
                                    <SovosBox
                                        sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: "success.light", flexShrink: 0, width: 100, display: "flex", justifyContent: "flex-end" }}
                                        title={i18n`How strong this proposed link is (0–100%)`}
                                    >
                                        <SovosTypography sx={{ fontSize: 10, color: "success.dark", fontWeight: 600 }}>
                                            {Math.round(score * 100)}%
                                        </SovosTypography>
                                    </SovosBox>
                                </SovosBox>
                            );
                        })}
                    </SovosBox>
                </SovosBox>
            )}

            <DataGrid
                key={`column-mapping-grid-${hideDoNotImport}`}
                id="column-mapping-grid"
                items={dgItems}
                header={dgHeader}
                card={rows.CellCard}
                status={{ loading: false }}
                metadata={{ total: dgItems.length }}
                enablePagination
                headerHeight={80}
                columnHeaderRenderer={columnHeaderRenderer}
                availableFilters={dgAvailableFilters}
                toolbar={MappingGridToolbar}
                toolbarProps={{ hideDoNotImport, onToggleHideDoNotImport, showDoNotImportToggle }}
                showCheckbox={false}
                modifierssx={{
                    "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                    "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                }}
            />

            <SovosDialog open={showAiMappingDialog} onClose={onCloseAiDialog} maxWidth="sm" fullWidth>
                <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                    <SovosIconButton size="small" tooltipText={i18n`Close`} onClick={onCloseAiDialog}>
                        <Close fontSize="small" />
                    </SovosIconButton>
                </SovosBox>
                <SovosDialogTitle>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AutoAwesome sx={{ fontSize: 18, color: "primary.main" }} />
                        {i18n`Column Mapping suggestions by Sovi`}
                    </SovosBox>
                </SovosDialogTitle>
                <SovosDialogContent>
                    {aiMappingResult && (
                        <SovosBox sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                            <SovosTypography variant="caption" color="text.primary" sx={{ display: "block", fontWeight: 600 }}>
                                {i18n`${aiMappingResult.column_mappings?.length ?? 0} proposed links in the table`}
                                {aiMappingResult.unmapped_source_columns?.length > 0
                                    ? ` · ${i18n`${aiMappingResult.unmapped_source_columns.length} CSV columns with no proposed target`}`
                                    : ""}
                                {aiMappingResult.warnings?.length
                                    ? ` · ${i18n`${aiMappingResult.warnings.length} notices`}`
                                    : ""}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {aiMappingResult?.warnings?.length > 0 && (
                        <SovosBox sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: "warning.light", border: "1px solid", borderColor: "warning.light" }}>
                            {aiMappingResult.warnings.map((w) => (
                                <SovosTypography key={w} variant="caption" color="warning.dark" sx={{ display: "block" }}>{w}</SovosTypography>
                            ))}
                        </SovosBox>
                    )}
                    <SovosBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <SovosBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: "action.selected",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>{i18n`CSV column`}</SovosTypography>
                            <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                            <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>{i18n`Suggested target`}</SovosTypography>
                            <SovosTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ width: 100, flexShrink: 0, textAlign: "right" }}
                                title={i18n`How strong this proposed link is (0–100%)`}
                            >
                                {i18n`Confidence`}
                            </SovosTypography>
                        </SovosBox>
                        {(aiMappingResult?.column_mappings || []).length === 0 && (
                            <SovosBox sx={{ px: 1.5, py: 2 }}>
                                <SovosTypography variant="caption" color="text.secondary">
                                    {i18n`No column-to-field links were proposed. Review the notices, map in the grid, or try suggestions again.`}
                                </SovosTypography>
                            </SovosBox>
                        )}
                        {(aiMappingResult?.column_mappings || []).map(({ source_column, target_field, display_name, score, rationale }, idx) => {
                            const resolvedId = resolveAiTargetToGridFieldId(target_field, mschFields);
                            const field = mschFields.find((f) => f.id === resolvedId);
                            const targetLabel = field?.name || display_name || target_field;
                            const pct = Math.round((score ?? 0) * 100);
                            return (
                                <SovosBox
                                    key={`${source_column}-${idx}`}
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
                                        <SovosTypography variant="caption" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap title={source_column}>{source_column}</SovosTypography>
                                        <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                                        <SovosTypography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap title={resolvedId}>{targetLabel}</SovosTypography>
                                    </SovosBox>
                                    <SovosBox sx={{ mb: rationale ? 0.75 : 0 }}>
                                        <SovosBox sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                                            <SovosTypography variant="caption" color="text.secondary">{i18n`Confidence score`}</SovosTypography>
                                            <SovosTypography variant="caption" fontWeight={600} sx={{ color: "primary.main" }}>{i18n`${pct}% confidence`}</SovosTypography>
                                        </SovosBox>
                                        <SovosBox sx={{ height: 4, borderRadius: 2, overflow: "hidden", bgcolor: "action.selected" }}>
                                            <SovosBox sx={{ width: `${pct}%`, height: "100%", background: AI_CONFIDENCE_GRADIENT }} />
                                        </SovosBox>
                                    </SovosBox>
                                    {rationale && (
                                        <SovosBox>
                                            <SovosTypography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block" }}>{i18n`AI Reasoning`}</SovosTypography>
                                            <SovosTypography variant="caption" color="text.secondary">{rationale}</SovosTypography>
                                        </SovosBox>
                                    )}
                                </SovosBox>
                            );
                        })}
                    </SovosBox>
                    {aiMappingResult?.unmapped_source_columns?.length > 0 && (
                        <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            {i18n`${aiMappingResult.unmapped_source_columns.length} columns could not be mapped`}:{" "}
                            {aiMappingResult.unmapped_source_columns.join(", ")}
                        </SovosTypography>
                    )}
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton variant="text" onClick={onCloseAiDialog}>{i18n`Discard`}</SovosButton>
                    <SovosButton
                        variant="contained"
                        onClick={onApplyAiMappings}
                        disabled={!(aiMappingResult?.column_mappings?.length > 0)}
                    >
                        {i18n`Apply to grid`}
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>
        </>
    );
};

export default StepMapColumns;
